import { FC, useContext, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "hooks";
import { setPageTitle, setPageType } from "store";
import { PageType } from "types";
import styles from "./styles.module.scss";
import ImageUploading, { ImageListType } from "react-images-uploading";
import { Button } from "@progress/kendo-react-buttons";
import {
  DropDownList,
  DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";
import Lottie from "lottie-react";
import car from "../../assets/car.json";
import {
  Card,
  CardBody,
  CardHeader,
  CardImage,
  CardTitle,
} from "@progress/kendo-react-layout";
import { Error } from "@progress/kendo-react-labels";
import { PDFExport } from "@progress/kendo-react-pdf";
import Slider from "react-slick";
import { SettingsContext } from "context";

const DROPDOWN_OPTIONS = [
  {
    label: "Sample 1",
    value: "https://dreamdemoassets.blob.core.windows.net/openai/Corvette.png",
    category: "General Motors",
    brand: "Chevrolet",
  },
  {
    label: "Sample 2",
    value: "https://dreamdemoassets.blob.core.windows.net/openai/Acadia.png",
    category: "General Motors",
    brand: "Acadia",
  },
  {
    label: "Sample 3",
    value: "https://dreamdemoassets.blob.core.windows.net/openai/Cadillac.png",
    category: "General Motors",
    brand: "Cadillac",
  },
  {
    label: "Sample 4",
    value: "https://dreamdemoassets.blob.core.windows.net/openai/Yukon.png",
    category: "General Motors",
    brand: "Yukon",
  },
];

const SCENARIO_OPTIONS = [
  {
    label: "Desert in daylight",
    value: "Desert in daylight",
  },
  {
    label: "Racetrack on a rainy day",
    value: "Racetrack on a rainy day",
  },
  {
    label: "Desert at nighttime",
    value: "Desert at nighttime",
  },
  {
    label: "City on a snowy day",
    value: "City on a snowy day",
  },
  {
    label: "Desert in a windy day",
    value: "Desert in a windy day",
  },
];

export const FlorenceModel: FC = () => {
  const dispatch = useAppDispatch();
  const container = useRef<PDFExport>(null);
  const { currentDemo } = useContext(SettingsContext);
  const [images, setImages] = useState<ImageListType>([]);

  useEffect(() => {
    dispatch(setPageType(PageType.FlorenceModel));
    dispatch(setPageTitle("Florence Model"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [result, setResult] = useState<any>({});

  const onChange = (imageList: ImageListType) => {
    setResult({});
    setModel("prebuilt-document");
    setIsProcessing(false);
    setImages(imageList as never[]);
  };
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [dalleImages, setDalleImages] = useState<string[]>([]);
  const [error, setError] = useState("");
  // const [popup, setPopup] = useState(false);

  const fetchData = (formData?: any) => {
    setIsLoading(true);
    fetch(
      currentDemo?.florenceAdApi ??
        "https://gm-demo-ad-text.azurewebsites.net/api/gm-demo",
      {
        method: "post",
        ...(formData && { body: formData }),
      }
    )
      .then((res) => res.json())
      .then((res) => {
        setResult(res);
        setIsImageLoading(true);
        fetch(
          currentDemo?.florenceDallEApi ??
            "https://gm-demo-dall-e.azurewebsites.net/api/GM-Demo-Dall-E",
          {
            method: "post",
            body: JSON.stringify({
              image_url: JSON.parse(formData).image_url,
              dalle_prompt: res.dalle_prompt,
            }),
          }
        )
          .then((res) => res.json())
          .then((res) => {
            setDalleImages([res.dalle_url, ...res.other_urls]);
            // setPopup(true);
          })
          .catch((e) => {
            setError(e.message);
          })
          .finally(() => setIsImageLoading(false));
      })
      .catch((e) => console.log(e))
      .finally(() => setIsLoading(false));
  };

  const [model, setModel] = useState("prebuilt-idDocument");

  const [value, setValue] = useState<{
    label: string;
    value: string;
    category: string;
    brand: string;
  }>(DROPDOWN_OPTIONS[0]);

  const [scenario, setScenario] = useState<{
    label: string;
    value: string;
  }>(SCENARIO_OPTIONS[0]);

  const [isProcessing, setIsProcessing] = useState(false);

  const handleChange = (event: DropDownListChangeEvent) => {
    setIsProcessing(false);
    setModel("prebuilt-document");
    setValue(DROPDOWN_OPTIONS.filter((d) => d.label === event.target.value)[0]);
    setDalleImages([]);
  };

  const handleScenarioChange = (event: DropDownListChangeEvent) => {
    setIsProcessing(false);
    setScenario(
      SCENARIO_OPTIONS.filter((d) => d.label === event.target.value)[0]
    );
    setDalleImages([]);
  };

  const onProcess = () => {
    setDalleImages([]);
    setIsProcessing(true);
    if (images?.length) {
      const formData: any = new FormData();
      const fileData = images[0].file;
      formData.append("file", fileData);
      formData.append("modelId", model);
      fetchData(formData);
    } else {
      const formData: any = new FormData();
      const image = value.value;
      formData.append("image_url", image);
      formData.append("product_category", value.category);
      fetchData(
        JSON.stringify({
          image_url: image,
          product_category: value.category,
          car_brand: value.brand,
          scenario: scenario.value,
        })
      );
    }
  };

  //preload images
  function preloadImage(src: string) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = function () {
        resolve(img);
      };
      img.onerror = img.onabort = function () {
        reject(src);
      };
      img.src = src;
    });
  }

  useEffect(() => {
    let isCancelled = false;

    async function effect() {
      if (isCancelled) {
        return;
      }

      const imagesPromiseList: Promise<any>[] = [];
      for (const i of DROPDOWN_OPTIONS) {
        imagesPromiseList.push(preloadImage(i.value));
      }

      await Promise.all(imagesPromiseList);

      if (isCancelled) {
        return;
      }
    }

    effect();

    return () => {
      isCancelled = true;
    };
  }, []);

  const [selectedImage, setSelectedImage] = useState(0);

  const exportPDFWithComponent = () => {
    if (container.current) {
      container.current.save();
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.dropDownContainer}>
        <DropDownList
          className={styles.dropDown}
          data={DROPDOWN_OPTIONS.map((d) => d.label)}
          value={value?.label}
          onChange={handleChange}
        />
        <DropDownList
          className={styles.dropDown}
          data={SCENARIO_OPTIONS.map((d) => d.label)}
          value={scenario?.label}
          onChange={handleScenarioChange}
        />
        {dalleImages.length > 0 && (
          <Button className={styles.btn} onClick={exportPDFWithComponent}>
            Print Generated Advertise
          </Button>
        )}
      </div>

      <div className={styles.tabContainer}>
        <div className={styles.tabImg}>
          <ImageUploading
            value={images}
            onChange={onChange}
            allowNonImageType={false}
            multiple={false}
          >
            {({ imageList, onImageUpload, onImageRemoveAll }) => {
              return (
                <div
                  className={`upload__image-wrapper ${styles.imageWrapper}`}
                  style={{ width: "100%", height: "100%" }}
                >
                  {imageList?.length > 0 && (
                    <span
                      onClick={() => {
                        !isLoading && setImages([]);
                        setValue(DROPDOWN_OPTIONS[0]);
                        setScenario(SCENARIO_OPTIONS[0]);
                      }}
                      className={`k-icon k-i-close ${styles.closeBtn}`}
                    ></span>
                  )}
                  {imageList?.map((image, index) => (
                    <div
                      key={index}
                      className="image-item"
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: "calc(100% - 48px)",
                      }}
                    >
                      <img
                        src={image.dataURL}
                        alt="uploaded-form"
                        style={{ height: "100%" }}
                      />
                    </div>
                  ))}
                  {images.length === 0 && (
                    <>
                      <img
                        src={value.value}
                        alt="uploaded-form"
                        style={{
                          height: "auto",
                          maxHeight: "calc(100% - 48px)",
                        }}
                      />
                    </>
                  )}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <Button
                      className={styles.btn}
                      onClick={onProcess}
                      id="#file"
                    >
                      Generate Advertise
                    </Button>
                  </div>
                </div>
              );
            }}
          </ImageUploading>
        </div>

        <div className={styles.tabText}>
          {isProcessing ? (
            <>
              {isLoading ? (
                <div className={styles.loaderContainer}>
                  <h2 className={styles.generatingVerbText}>
                    Generating Verbiage...
                  </h2>
                  <Lottie
                    animationData={car}
                    loop={true}
                    style={{
                      height: 350,
                      width: 400,
                    }}
                  />
                </div>
              ) : (
                <div className={styles.textContainer}>
                  <Card className={styles.cardContainer}>
                    <CardHeader>
                      <CardTitle>
                        {result?.advertisement
                          ?.split("\n")[0]
                          .split(":")?.[1]
                          ?.trim()}
                      </CardTitle>
                    </CardHeader>
                    <CardBody className={styles.cardBody}>
                      <div>
                        <p>
                          {result?.advertisement
                            ?.split("\n")
                            ?.slice(1)
                            ?.join("")
                            ?.split(":")?.[1]
                            ?.trim()}
                        </p>
                      </div>
                      <div>
                        {isImageLoading ? (
                          <div className={styles.generatedLoader}>
                            <Lottie
                              animationData={car}
                              loop={true}
                              style={{
                                height: 350,
                                width: 400,
                              }}
                            />
                            <h2 className={styles.generatingText}>
                              Generating Image...
                            </h2>
                          </div>
                        ) : !images ? (
                          <Error className={styles.error}>
                            Oops! Something went wrong. Please try again later.{" "}
                            <br />
                            Error: {error}
                          </Error>
                        ) : (
                          <div
                            style={{
                              position: "relative",
                              justifyContent: "center",
                              alignItems: "center",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <div
                              style={{
                                height: 350,
                                width: 425,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <img
                                style={{
                                  height: 350,
                                  width: 425,
                                  borderRadius: 8,
                                }}
                                src={dalleImages[selectedImage]}
                                alt="img"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </div>
              )}
            </>
          ) : null}
        </div>

        <div
          style={{
            position: "absolute",
            left: -10000,
            top: 0,
          }}
        >
          <PDFExport
            paperSize="A4"
            landscape={true}
            margin="20"
            scale={1} // 1.1 for portrait
            ref={container}
            fileName={result?.advertisement
              ?.split("\n")[0]
              .split(":")[1]
              .trim()}
          >
            <div className={styles.tabText}>
              {isProcessing ? (
                <>
                  {isLoading ? (
                    <div className={styles.loaderContainer}>
                      <h2 className={styles.generatingVerbText}>
                        Generating Verbiage...
                      </h2>
                      <Lottie
                        animationData={car}
                        loop={true}
                        style={{
                          height: 350,
                          width: 400,
                        }}
                      />
                    </div>
                  ) : (
                    <div className={styles.textContainer}>
                      <Card className={styles.cardContainer}>
                        <CardHeader>
                          <CardTitle>
                            {result?.advertisement
                              ?.split("\n")[0]
                              .split(":")?.[1]
                              ?.trim()}
                          </CardTitle>
                        </CardHeader>
                        <CardBody className={styles.cardBody}>
                          <div>
                            <p>
                              {result?.advertisement
                                ?.split("\n")
                                ?.slice(1)
                                ?.join("")
                                ?.split(":")?.[1]
                                ?.trim()}
                            </p>
                          </div>
                          <div>
                            {isImageLoading ? (
                              <div className={styles.generatedLoader}>
                                <Lottie
                                  animationData={car}
                                  loop={true}
                                  style={{
                                    height: 350,
                                    width: 400,
                                  }}
                                />
                                <h2 className={styles.generatingText}>
                                  Generating Image...
                                </h2>
                              </div>
                            ) : !images ? (
                              <Error className={styles.error}>
                                Oops! Something went wrong. Please try again
                                later.
                                <br />
                                Error: {error}
                              </Error>
                            ) : (
                              <div
                                style={{
                                  position: "relative",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <div
                                  style={{
                                    height: 350,
                                    width: 425,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <img
                                    style={{
                                      height: 350,
                                      width: 425,
                                      borderRadius: 8,
                                    }}
                                    src={dalleImages[selectedImage]}
                                    alt="img"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </PDFExport>
        </div>
      </div>

      {dalleImages.length > 0 && (
        <div className={styles.tabText}>
          <div className={styles.textContainer} style={{ marginTop: 20 }}>
            <Card className={styles.carouselCardContainer}>
              <CardHeader>
                <CardTitle>Generated Images</CardTitle>
              </CardHeader>
              <CardBody
                style={{
                  height: 290,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Slider
                  arrows={true}
                  dots={true}
                  lazyLoad="progressive"
                  autoplay={false}
                  slidesToShow={5}
                  infinite={false}
                  className={styles.slick}
                  pauseOnFocus
                  pauseOnHover
                >
                  {dalleImages?.map((item, index) => {
                    return (
                      <div
                        onClick={() => {
                          setSelectedImage(index);
                        }}
                        key={index}
                        className={styles.slideContainer}
                      >
                        <div
                          className={`${styles.overlay} ${
                            selectedImage === index && styles.showArrow
                          }`}
                        >
                          <span
                            className={`k-icon k-i-check-circle ${styles.checkIcon}`}
                          ></span>
                        </div>
                        <CardImage
                          style={{ height: 260, width: 260, borderRadius: 8 }}
                          src={item}
                        />
                      </div>
                    );
                  })}
                </Slider>
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlorenceModel;
